import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@spark/ui-components/components/Icon';

const actionButtonStyle = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};

const stopPropagation = (event) => {
  event.stopPropagation();
};

const InlineDateTimeCell = ({
  value,
  isEditing,
  isSaving,
  editingValue,
  onStartEdit,
  onCommit,
  onCancel
}) => {
  const inputRef = useRef(null);
  const didFinishRef = useRef(false);
  const wasEditingRef = useRef(false);
  const [localValue, setLocalValue] = useState('');

  useEffect(() => {
    if (!isEditing || isSaving) {
      didFinishRef.current = false;
      wasEditingRef.current = false;
      return;
    }

    if (!wasEditingRef.current) {
      setLocalValue(editingValue ?? '');
    }

    wasEditingRef.current = true;
    didFinishRef.current = false;

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isEditing, isSaving, editingValue]);

  const handleCommit = () => {
    if (didFinishRef.current || isSaving) return;
    didFinishRef.current = true;
    onCommit(localValue);
  };

  const handleCancel = () => {
    if (didFinishRef.current || isSaving) return;
    didFinishRef.current = true;
    onCancel();
  };

  const handleBlur = (event) => {
    const form = event.currentTarget.form;
    if (form && event.relatedTarget && form.contains(event.relatedTarget)) {
      return;
    }

    handleCommit();
  };

  if (isSaving) {
    return (
      <div
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 28 }}
        onClick={stopPropagation}
      >
        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
        <span>Salvando...</span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          stopPropagation(event);
          handleCommit();
        }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 240 }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <input
          ref={inputRef}
          type="datetime-local"
          value={localValue}
          onChange={(event) => setLocalValue(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={(event) => {
            event.stopPropagation();

            if (event.key === 'Escape') {
              event.preventDefault();
              handleCancel();
            }
          }}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          onPointerDown={stopPropagation}
          style={{
            width: '100%',
            minWidth: 180,
            height: 30,
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: '0 8px'
          }}
        />
        <button
          type="button"
          aria-label="Limpar data"
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => event.preventDefault()}
          onClick={(event) => {
            stopPropagation(event);
            setLocalValue('');
          }}
          style={actionButtonStyle}
        >
          <Icon name="close" size={14} />
        </button>
        <button
          type="button"
          aria-label="Cancelar edicao"
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => event.preventDefault()}
          onClick={(event) => {
            stopPropagation(event);
            handleCancel();
          }}
          style={actionButtonStyle}
        >
          <Icon name="close-circle-outline" size={16} />
        </button>
        <button
          type="submit"
          aria-label="Salvar edicao"
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={(event) => event.preventDefault()}
          onClick={stopPropagation}
          style={{ ...actionButtonStyle, color: '#198754' }}
        >
          <Icon name="check-circle-outline" size={16} />
        </button>
      </form>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(event) => {
        stopPropagation(event);
        onStartEdit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          stopPropagation(event);
          onStartEdit();
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        minHeight: 24,
        cursor: 'pointer'
      }}
      title="Clique para editar"
    >
      <span>{value || '-'}</span>
      <Icon name="pencil" size={12} />
    </div>
  );
};

InlineDateTimeCell.propTypes = {
  value: PropTypes.string,
  isEditing: PropTypes.bool,
  isSaving: PropTypes.bool,
  editingValue: PropTypes.string,
  onStartEdit: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

InlineDateTimeCell.defaultProps = {
  value: '',
  isEditing: false,
  isSaving: false,
  editingValue: ''
};

export default InlineDateTimeCell;
